import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";

import messages from "../../../../assets/locale/messages";
import {
  createCompanyEvaluationRequest,
  getCompanyEvaluationsRequest,
} from "../../../../store/CompanyPortal/Evaluations/actions";
import { EVALUATION_ROUND_STATUSES } from "./constants";

const DEFAULT_PAGE_SIZE = 10;

const isSameOrAfter = (rowDate, filterDate) => {
  const d = dayjs(rowDate);
  const f = dayjs(filterDate);
  return d.isValid() && f.isValid() && (d.isSame(f, "day") || d.isAfter(f, "day"));
};

const isSameOrBefore = (rowDate, filterDate) => {
  const d = dayjs(rowDate);
  const f = dayjs(filterDate);
  return d.isValid() && f.isValid() && (d.isSame(f, "day") || d.isBefore(f, "day"));
};

// Rounds per event are few, so we fetch the full list (page=-1) and keep the
// search / status / date filtering and pagination on the client.
const useEvaluationRounds = (eventId, portalVariant = "company-portal") => {
  const dispatch = useDispatch();
  const lang = useSelector((state) => state.locale.lang);
  const allRounds = useSelector(
    (state) => state.companyEvaluations.rounds.list,
  );
  const t = messages[lang].events.viewEvents.evaluationsTab;

  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    status: null,
    startDate: null,
    endDate: null,
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const listParams = useMemo(
    () => ({
      event_id: eventId,
      page: -1,
      order_by_created_at: "desc",
      portal: portalVariant,
    }),
    [eventId, portalVariant],
  );

  useEffect(() => {
    if (eventId) dispatch(getCompanyEvaluationsRequest(listParams));
  }, [dispatch, eventId, listParams]);

  const statusOptions = useMemo(
    () => [
      {
        id: EVALUATION_ROUND_STATUSES.running,
        name: t.table.statuses.running,
      },
      {
        id: EVALUATION_ROUND_STATUSES.completed,
        name: t.table.statuses.completed,
      },
    ],
    [t],
  );

  const hasAnyRounds = allRounds.length > 0;

  const filteredRows = useMemo(() => {
    let rows = allRounds;

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter((r) => r.name.toLowerCase().includes(q));
    }

    if (filters.status) {
      rows = rows.filter((r) => r.status === filters.status.id);
    }

    if (filters.startDate) {
      rows = rows.filter((r) => isSameOrAfter(r.startDate, filters.startDate));
    }

    if (filters.endDate) {
      rows = rows.filter(
        (r) => r.endDate && isSameOrBefore(r.endDate, filters.endDate),
      );
    }

    return rows;
  }, [allRounds, search, filters]);

  const total = filteredRows.length;
  const pages = Math.ceil(total / pageSize) || 1;
  const safePageIndex = Math.min(page, pages);
  const rangeFrom = total === 0 ? 0 : (safePageIndex - 1) * pageSize + 1;
  const rangeTo = Math.min(safePageIndex * pageSize, total);
  const paginatedRows = filteredRows.slice(
    (safePageIndex - 1) * pageSize,
    safePageIndex * pageSize,
  );

  const runningRound = allRounds.find(
    (r) => r.status === EVALUATION_ROUND_STATUSES.running,
  );

  const allCompleted =
    allRounds.length > 0 &&
    allRounds.every((r) => r.status === EVALUATION_ROUND_STATUSES.completed);

  const startRound = useCallback(() => {
    if (!eventId) return;
    dispatch(createCompanyEvaluationRequest({ eventId, listParams }));
    setPage(1);
  }, [dispatch, eventId, listParams]);

  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(1);
  };

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPage(1);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPage(1);
  };

  return {
    search,
    filters,
    page,
    pageSize,
    rows: paginatedRows,
    total,
    pages,
    rangeFrom,
    rangeTo,
    hasAnyRounds,
    runningRound,
    allCompleted,
    statusOptions,
    startRound,
    handleSearchChange,
    handleFilterChange,
    handlePageChange: setPage,
    handlePageSizeChange,
  };
};

export default useEvaluationRounds;
